// src/context/Theme/ThemeContextData.tsx
import React, { createContext, useState, useContext, ReactNode } from 'react'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'
import { ThemeId, darkTheme, lightTheme } from '../../styles/themes'

export type MenuOrientation = 'horizontal' | 'vertical'
export type MenuHighlight = 'green' | 'mixed'

interface ThemeContextData {
  theme: ThemeId
  setTheme: (theme: ThemeId) => void
  toggleTheme: () => void
  menuOrientation: MenuOrientation
  setMenuOrientation: (orientation: MenuOrientation) => void
  menuHighlight: MenuHighlight
  setMenuHighlight: (highlight: MenuHighlight) => void
}

const CustomThemeContext = createContext<ThemeContextData>({} as ThemeContextData)

interface CustomThemeProviderProps {
  children: ReactNode
}

export function CustomThemeProvider({ children }: CustomThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeId>(() => {
    const saved = localStorage.getItem('@GID:theme')
    return saved === 'light' || saved === 'dark' ? saved : 'dark'
  })

  const [menuOrientation, setMenuOrientationState] = useState<MenuOrientation>(() => {
    const saved = localStorage.getItem('@GID:menuOrientation')
    return saved === 'vertical' || saved === 'horizontal' ? saved : 'horizontal'
  })

  const [menuHighlight, setMenuHighlightState] = useState<MenuHighlight>(() => {
    const saved = localStorage.getItem('@GID:menuHighlight')
    return saved === 'mixed' || saved === 'green' ? saved : 'green'
  })

  const setTheme = (newTheme: ThemeId) => {
    setThemeState(newTheme)
    localStorage.setItem('@GID:theme', newTheme)
  }

  const toggleTheme = () => {
    setThemeState((prev) => {
      const next: ThemeId = prev === 'dark' ? 'light' : 'dark'
      localStorage.setItem('@GID:theme', next)
      return next
    })
  }

  const setMenuOrientation = (orientation: MenuOrientation) => {
    setMenuOrientationState(orientation)
    localStorage.setItem('@GID:menuOrientation', orientation)
  }

  const setMenuHighlight = (highlight: MenuHighlight) => {
    setMenuHighlightState(highlight)
    localStorage.setItem('@GID:menuHighlight', highlight)
  }

  const currentTheme = theme === 'light' ? lightTheme : darkTheme

  return (
    <CustomThemeContext.Provider
      value={{
        theme,
        setTheme,
        toggleTheme,
        menuOrientation,
        setMenuOrientation,
        menuHighlight,
        setMenuHighlight,
      }}
    >
      <StyledThemeProvider theme={currentTheme}>{children}</StyledThemeProvider>
    </CustomThemeContext.Provider>
  )
}

export const useCustomTheme = () => useContext(CustomThemeContext)
